import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const PARSE_TIMEOUT_MS = 8000;

/**
 * VoiceMicHook — Web Speech API mic pill.
 * Live mode (value + onLiveChange): words stream into the field as spoken; each
 * finished sentence is translated/cleaned in the background and swapped in place.
 * Structured mode (onParsed only): final transcript parsed into JSON after a pause.
 */
export default function VoiceMicHook({ target = 'medicine', onParsed, onUsed, label = 'Dictate', value, onLiveChange }) {
  const live = typeof onLiveChange === 'function';
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [parsing, setParsing] = useState(false);
  const [pending, setPending] = useState(0);
  const [err, setErr] = useState('');
  const recRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const bufferRef = useRef('');
  const baseRef = useRef('');
  const segsRef = useRef([]);
  const interimRef = useRef('');

  const SR = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;

  useEffect(() => {
    return () => { try { recRef.current && recRef.current.stop(); } catch (_) {} };
  }, []);

  const parseText = (text) => api.post('/dashboard/voice/parse-dhf', { transcript: text, target }, { timeout: PARSE_TIMEOUT_MS });

  const emitLive = () => {
    const spoken = [...segsRef.current.map(s => s.text), interimRef.current.trim()].filter(Boolean).join(' ');
    const base = baseRef.current;
    onLiveChange(spoken ? (base ? `${base}\n${spoken}` : spoken) : base);
  };

  const translateSegment = async (seg) => {
    setPending(n => n + 1);
    try {
      const { data } = await parseText(seg.raw);
      const out = data?.ok && data.parsed ? Object.values(data.parsed).find(v => typeof v === 'string' && v.trim()) : '';
      if (out) { seg.text = out.trim(); emitLive(); }
    } catch (_) {
      // keep raw spoken text on timeout/failure
    } finally { setPending(n => Math.max(0, n - 1)); }
  };

  const flush = async () => {
    const text = (bufferRef.current || '').trim();
    bufferRef.current = '';
    setInterim('');
    if (!text) return;
    setParsing(true); setErr('');
    try {
      const { data } = await parseText(text);
      if (data.ok && data.parsed) {
        onParsed && onParsed(data.parsed);
        onUsed && onUsed();
      } else {
        setErr('Could not parse — please try again or type it in.');
      }
    } catch (e) {
      setErr(e?.code === 'ECONNABORTED' ? 'AI took too long — please retry or type it in.' : 'Parse failed — please retry.');
    } finally { setParsing(false); }
  };

  const handleResultLive = (evt) => {
    let interimStr = '';
    for (let i = evt.resultIndex; i < evt.results.length; i++) {
      const t = evt.results[i][0].transcript.trim();
      if (evt.results[i].isFinal) {
        if (!t) continue;
        const seg = { raw: t, text: t };
        segsRef.current.push(seg);
        onUsed && onUsed();
        translateSegment(seg);
      } else {
        interimStr += t + ' ';
      }
    }
    interimRef.current = interimStr;
    setInterim(interimStr);
    emitLive();
  };

  const handleResultStructured = (evt) => {
    let interimStr = '';
    for (let i = evt.resultIndex; i < evt.results.length; i++) {
      const t = evt.results[i][0].transcript;
      if (evt.results[i].isFinal) bufferRef.current += (bufferRef.current ? ' ' : '') + t.trim();
      else interimStr += t;
    }
    setInterim(interimStr);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => { if (bufferRef.current.trim()) flush(); }, 1600);
  };

  const start = () => {
    if (!SR) { setErr('Voice not supported in this browser. Try Chrome or Edge.'); return; }
    setErr('');
    bufferRef.current = '';
    segsRef.current = [];
    interimRef.current = '';
    baseRef.current = (value || '').trimEnd();
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-IN';
    rec.onresult = live ? handleResultLive : handleResultStructured;
    rec.onerror = (e) => {
      if (e?.error !== 'no-speech' && e?.error !== 'aborted') setErr(`Mic error: ${e?.error || 'unknown'}`);
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
      setInterim('');
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      if (live) { interimRef.current = ''; emitLive(); return; }
      if (bufferRef.current.trim()) flush();
    };
    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (e) {
      setErr('Could not start mic — check permissions.');
    }
  };

  const stop = () => {
    try { recRef.current && recRef.current.stop(); } catch (_) {}
    setListening(false);
  };

  if (!SR) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 italic px-2" title="Web Speech API not available">
        <MicOff size={11} /> Voice needs Chrome/Edge
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2 flex-wrap" data-testid={`voice-mic-${target}`}>
      <button
        type="button"
        onClick={listening ? stop : start}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border transition-colors ${
          listening
            ? 'bg-rose-500 border-rose-500 text-white animate-pulse'
            : 'bg-white border-sky-300 text-sky-700 hover:bg-sky-50'
        }`}
        data-testid={`voice-mic-btn-${target}`}
        title={listening ? 'Tap to stop' : 'Tap to dictate'}
      >
        <Mic size={11} />
        {listening ? 'Listening… tap to stop' : label}
      </button>
      {parsing && (
        <span className="inline-flex items-center gap-1 text-[10px] text-slate-500" data-testid={`voice-parsing-${target}`}>
          <Loader2 size={10} className="animate-spin" /> Parsing…
        </span>
      )}
      {live && pending > 0 && (
        <span className="inline-flex items-center gap-1 text-[10px] text-sky-600" data-testid={`voice-translating-${target}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" /> translating…
        </span>
      )}
      {!live && interim && listening && (
        <span className="text-[10px] text-slate-500 italic truncate max-w-[240px]" data-testid={`voice-interim-${target}`}>
          &quot;{interim.slice(0, 60)}{interim.length > 60 ? '…' : ''}&quot;
        </span>
      )}
      {err && <span className="text-[10px] text-rose-600" data-testid={`voice-err-${target}`}>{err}</span>}
    </span>
  );
}
